import useChallengesQuery from "@hooks/common/challenges/use-challenges-query";
import { ChallengeLeagueTags } from "@Enums/index";
import { ChallengeResponse, GetChallengesRequest } from "@Shared/schemas/challenges/challengeSchemas";
import { Skeleton, Stack, useTheme } from "@mui/material";
import { SwiperSlide } from "swiper/react";
import { HorizontalScroller } from "../common/horizontal-scroller";
import ChallengeCardSimple, { IChallengeCardSimpleBase } from "./cards/challenge-card-simple";
import { getFirstNonCompletedChallenge } from "./utils";

interface IChallengeRow extends IChallengeCardSimpleBase {
  tag?: string;
  limit?: number;
  filters?: GetChallengesRequest["filters"];
  orderBy?: GetChallengesRequest["order_by"];
  overflow?: boolean;
  excludedTags?: string[];
}

const ChallengeRow = ({
  tag,
  size = "medium",
  limit,
  filters = {},
  orderBy = [
    {
      field: "unlocked_at",
      direction: "asc",
    },
  ],
  overflow = true,
  forceMaxWidth = true,
  excludedTags = [ChallengeLeagueTags.QA, ChallengeLeagueTags.DEVELOPMENT],
}: IChallengeRow) => {
  const theme = useTheme();

  const { challenges, isPending: loading } = useChallengesQuery({
    exclude_tags: excludedTags,
    filters,
    order_by: orderBy,
    ...(tag && { include_tags: [tag] }),
    ...(limit && { limit }),
  });

  return !loading && challenges ? (
    challenges.length > 0 ? (
      overflow ? (
        <HorizontalScroller padding={theme.spacing(6)} initialSlide={getFirstNonCompletedChallenge(challenges)}>
          {challenges.map((challenge: ChallengeResponse, index: number) => {
            return (
              <SwiperSlide key={challenge.id} style={{ width: "auto", marginLeft: theme.spacing(index === 0 ? 6 : 3) }}>
                <ChallengeCardSimple challenge={challenge} size={size} />
              </SwiperSlide>
            );
          })}
        </HorizontalScroller>
      ) : (
        <Stack direction="row" spacing={3} sx={{ padding: theme.spacing(0, 6) }}>
          {challenges.map((challenge: ChallengeResponse, index: number) => {
            return <ChallengeCardSimple key={challenge.id} challenge={challenge} fullWidth={!overflow} forceMaxWidth={forceMaxWidth} />;
          })}
        </Stack>
      )
    ) : null
  ) : (
    <Stack spacing={3}>
      <HorizontalScroller padding={theme.spacing(6)}>
        {[...Array(4)].map((_, index) => (
          <SwiperSlide key={index} style={{ width: "auto", marginLeft: theme.spacing(6) }}>
            <Stack spacing={2}>
              <Skeleton
                variant="rounded"
                height={size === "medium" ? 244 : 180}
                width={size === "medium" ? 432 : 318}
                sx={{ borderRadius: theme.spacing(3) }}
              />
              <Stack spacing={0.5}>
                <Skeleton variant="text" height={32} width={250} />
                <Skeleton variant="text" height={24} width={150} />
              </Stack>
            </Stack>
          </SwiperSlide>
        ))}
      </HorizontalScroller>
    </Stack>
  );
};

export default ChallengeRow;
